import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useMemo, useState } from "react";
import CustomSelect from "../../../../../../components/common/Select";
import { MemoryMessage } from "../hooks/useMemoryMessages";

interface Contact {
  email: string;
  name: string;
  source: "generic" | "youtube";
  count: number;
}

interface ContactsTabProps {
  messages: MemoryMessage[];
}

function ContactCard({
  contact,
  checked,
  onCheck,
}: {
  contact: Contact;
  checked: boolean;
  onCheck: () => void;
}) {
  const initials = (contact.name ?? "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const sourceLabel =
    contact.source === "youtube" ? "Youtube" : "Memorial Page";
  const sourceColor = contact.source === "youtube" ? "#ef4444" : "#707070";
  const sourceBg = contact.source === "youtube" ? "#fef2f2" : "#e3e0e0";

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        p: 2,
        boxShadow: 0,
        bgcolor: "#f0f4f8",
        position: "relative",
      }}
    >
      <Checkbox
        checked={checked}
        onChange={onCheck}
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          p: 0,
          color: "#cbd5e1",
          "&.Mui-checked": { color: "#724e41" },
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1,
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: "#8E8E8E",
            fontSize: "16px",
            fontWeight: 700,
            mb: 1,
          }}
        >
          {initials}
        </Avatar>

        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#1e293b",
            textAlign: "center",
            mb: 0.5,
          }}
        >
          {contact.name || "Anonymous"}
        </Typography>

        <Chip
          label={sourceLabel}
          size="small"
          sx={{
            bgcolor: sourceBg,
            color: sourceColor,
            fontWeight: 600,
            fontSize: "12px",
            height: 18,
            borderRadius: "4px",
            mb: 1.5,
          }}
        />

        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
          <Box
            component="a"
            href={contact.email ? `mailto:${contact.email}` : undefined}
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: contact.email ? "pointer" : "default",
              textDecoration: "none",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 24, color: "#8E8E8E" }} />
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function ContactsTab({ messages }: ContactsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [checkedEmails, setCheckedEmails] = useState<string[]>([]);

  // Derive contacts from messages — deduplicate by email
  const contacts = useMemo<Contact[]>(() => {
    const map = new Map<string, Contact>();
    messages.forEach((msg) => {
      const key = msg.sender_email?.toLowerCase() ?? `no-email-${msg.id}`;
      if (map.has(key)) {
        map.get(key)!.count += 1;
      } else {
        map.set(key, {
          email: msg.sender_email ?? "",
          name: msg.sender_full_name ?? "Anonymous",
          source: msg.comment_source,
          count: 1,
        });
      }
    });
    return Array.from(map.values());
  }, [messages]);

  const filtered = useMemo(() => {
    return contacts.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource =
        filterSource === "all" || c.source === filterSource;
      return matchesSearch && matchesSource;
    });
  }, [contacts, searchQuery, filterSource]);

  const toggleCheck = (email: string) => {
    setCheckedEmails((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email],
    );
  };

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2.5, width: "fit-content" }}
      >
        <Box sx={{ width: 200, flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Search here..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
              },
            }}
          />
        </Box>

        <CustomSelect
          onChange={(e) => setFilterSource(String(e.target.value))}
          label=""
          defaultValue="all"
          sx={{ width: 150 }}
          size="small"
        >
          <MenuItem value="all">All Contacts</MenuItem>
          <MenuItem value="generic">Memorial Page</MenuItem>
          <MenuItem value="youtube">Youtube</MenuItem>
        </CustomSelect>

        <CustomSelect
          label="Bulk Actions"
          defaultValue="email"
          sx={{ width: 150 }}
          size="small"
          onChange={() => {}}
        >
          <MenuItem value="email">Email</MenuItem>
        </CustomSelect>
      </Stack>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
            No contacts found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {filtered.map((contact) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={contact.email}>
              <ContactCard
                contact={contact}
                checked={checkedEmails.includes(contact.email)}
                onCheck={() => toggleCheck(contact.email)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}